import React, { useState, useCallback, useRef, useEffect } from 'react'
import BookingsView from './CustomerPortal/BookingsView'
import CustomerSubscription from './CustomerPortal/CustomerSubscription'
import CustomerSettings from './CustomerPortal/CustomerSettings'
import { Switch, Route, withRouter } from 'react-router-dom'
import SettingsActions from 'Redux/SettingsRedux'
import { useSelector, useDispatch } from 'react-redux'
import AuthAction from 'Redux/AuthRedux'
import {
  ActionList,
  ContextualSaveBar,
  Frame,
  Navigation,
  Page,
  Toast,
  TopBar,
} from '@shopify/polaris'
import {
  HomeIcon,
  OrderIcon,
  MarketsIcon,
  ArrowLeftIcon,
} from '@shopify/polaris-icons'
import { useParams, useHistory } from 'react-router-dom'
import Login from './CustomerPortal/Login'
import { getString, removeItem, setString } from '../Themes/constants'
import CustomerBookingDetails from './CustomerPortal/CustomerBookingDetails'
import CustomerSubscriptionDetails from './CustomerPortal/CustomerSubscriptionDetails'
import axios from 'axios'
import Loader from 'Components/Loader'

const pathToRouteMap = [
  {
    path: '/apps/customer_portal/upcoming_bookings',
    component: BookingsView,
    extraData: { status: 'upcoming' },
    card: true,
    name: 'Upcoming Bookings',
  },
  {
    path: '/apps/customer_portal/past_bookings',
    component: BookingsView,
    extraData: { status: 'past' },
    card: true,
    name: 'Past Bookings',
  },
  {
    path: '/apps/customer_portal/subscription_bookings',
    component: CustomerSubscription,
    card: true,
    name: 'Subscription',
  },
  {
    path: '/apps/customer_portal/customer_settings',
    component: CustomerSettings,
    card: true,
    name: 'Settings',
  },
  {
    path: '/apps/customer_portal/booking_details/:id',
    component: CustomerBookingDetails,
    card: true,
    name: 'Booking Details',
  },
  {
    path: '/apps/customer_portal/subscription_bookings/:id',
    component: CustomerSubscriptionDetails,
    card: true,
    name: 'Subscription Details',
  },
]

function CustomerPortalMain(props) {
  const skipToContentRef = useRef(null)
  const [toggle, setToggle] = useState(false)
  const dispatch = useDispatch()
  const [toastActive, setToastActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userMenuActive, setUserMenuActive] = useState(false)
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false)
  const [modalActive, setModalActive] = useState(false)
  const settingsData = useSelector((state) => state.settings.data)
  const [logo, setLogo] = useState('')

  const history = useHistory()

  const toggleToastActive = useCallback(
    () => setToastActive((toastActive) => !toastActive),
    []
  )
  const toggleUserMenuActive = useCallback(
    () => setUserMenuActive((userMenuActive) => !userMenuActive),
    []
  )

  const toggleMobileNavigationActive = useCallback(
    () =>
      setMobileNavigationActive(
        (mobileNavigationActive) => !mobileNavigationActive
      ),
    []
  )
  const toggleIsLoading = useCallback(
    () => setIsLoading((isLoading) => !isLoading),
    []
  )
  const toggleModalActive = useCallback(
    () => setModalActive((modalActive) => !modalActive),
    []
  )

  const toastMarkup = toastActive ? (
    <Toast onDismiss={toggleToastActive} content="Changes saved" />
  ) : null

  const userMenuActions = [
    {
      items: [
        {
          content: 'Logout',
          onAction: () => {
            removeItem('token')
            window.location.href = '/'
          },
        },
      ],
    },
  ]

  const userMenuMarkup = (
    <TopBar.UserMenu
      actions={userMenuActions}
      name={props.email}
      detail={props.email}
      initials={props.email && props.email[0].toUpperCase()}
      open={userMenuActive}
      onToggle={toggleUserMenuActive}
    />
  )

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      onNavigationToggle={toggleMobileNavigationActive}
    />
  )

  const logoMarkup = logo && {
    topBarSource: logo,
    width: 80,
    height: 55,
    url: logo,
  }

  const navigationMarkup = (
    <Navigation location="/">
      <Navigation.Section
        items={[
          {
            label: 'Back to Store',
            icon: ArrowLeftIcon,
            url: `https://${props.shopify_domain}`,
          },
        ]}
      />
      <Navigation.Section
        separator
        title="Bookings"
        items={[
          {
            url: '/apps/customer_portal/upcoming_bookings',
            label: 'Upcoming Bookings',
            icon: HomeIcon,
          },
          {
            url: '/apps/customer_portal/past_bookings',
            label: 'Past Bookings',
            icon: OrderIcon,
          },
          {
            url: '/apps/customer_portal/subscription_bookings',
            label: 'Subscription Bookings',
            icon: MarketsIcon,
          },
        ]}
      />
    </Navigation>
  )

  useEffect(() => {
    const token = getString('token')
    const url = new URL(window.location.href)
    const appointo_key = url.searchParams.get('appointo_key')
    const isTokenOrKeyAvailable = token || appointo_key

    if (isTokenOrKeyAvailable) {
      // Only call once based on the presence of appointo_key or token
      const authToken = appointo_key || token
      dispatch(
        AuthAction.me(authToken, (resp) => {
          if (resp.data && resp.data.shopify_domain) {
            dispatch(SettingsActions.setSettings(resp.data))
            // Handle redirect based on appointo_key only if it's in the URL
            if (appointo_key) {
              setString('token', appointo_key)
              const id = window.location.href.split('/').pop()
              if (window.location.href.includes('/bookings')) {
                history.push(`/apps/customer_portal/booking_details/${id}`)
              } else if (window.location.href.includes('/subscriptions')) {
                history.push(
                  `/apps/customer_portal/subscription_bookings/${id}`
                )
              } else {
                history.push(`/apps/customer_portal/upcoming_bookings`)
              }
            }
          }
        })
      )
    } else {
      // Redirect to login if not logged in and no token is found
      if (!window.location.href.includes('/apps/customer_portal/signin')) {
        window.location.href = '/apps/customer_portal/signin'
      }
    }
  }, [dispatch, history])

  useEffect(() => {
    setLogo(settingsData?.logo ? settingsData?.logo : settingsData?.email_logo)
  }, [settingsData])

  const token = getString('token')
  window.customer_token = token

  useEffect(() => {
    if (props.locale !== 'en') {
      const googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            layout: google.translate.TranslateElement.InlineLayout.HORIZONTAL,
          },
          'google_translate_element'
        )
      }
      var addScript = document.createElement('script')
      addScript.setAttribute(
        'src',
        '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
      )
      document.body.appendChild(addScript)
      window.googleTranslateElementInit = googleTranslateElementInit

      const getLanguage = async () => {
        let resp = await axios.get(
          `${process.env.BASE_URL}/customer_portal/login/get_language?shopify_domain=${props.shopify_domain}`
        )

        let locale = resp.data && resp.data.locale ? resp.data.locale : 'en'
        setIsLoading(true)

        while (true) {
          let element = document.getElementById('google_translate_element')
          if (!!element) {
            break
          }
        }

        function setCookie(key, value, expiry) {
          var expires = new Date()
          expires.setTime(expires.getTime() + expiry * 24 * 60 * 60 * 1000)
          document.cookie =
            key + '=' + value + ';expires=' + expires.toUTCString()
        }

        function getCookie(cname) {
          let name = cname + '='
          let decodedCookie = decodeURIComponent(document.cookie)
          let ca = decodedCookie.split(';')
          for (let i = 0; i < ca.length; i++) {
            let c = ca[i]
            while (c.charAt(0) == ' ') {
              c = c.substring(1)
            }
            if (c.indexOf(name) == 0) {
              return c.substring(name.length, c.length)
            }
          }
          return ''
        }

        if (getCookie('googtrans') !== `/en/${locale}`) {
          setCookie('googtrans', `/en/${locale}`, 1)
          window.location.reload()
        } else {
          setIsLoading(false)
        }
      }

      getLanguage()

      setTimeout(() => {
        let el = document.getElementsByClassName('goog-te-combo')
        if (el.length > 0) {
          el[0].value = 'en'
        }

        let element = document.getElementById('google_translate_element')
        element.style.display = 'none'
      }, 200)
    }
  }, [dispatch])

  return (
    <>
      <div id="google_translate_element"></div>

      <Switch>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Route exact path="/apps/customer_portal/signin">
              <Login
                shopify_domain={props.shopify_domain}
                setToggle={setToggle}
                {...props}
              />
            </Route>
            {!!token && (
              <Frame
                topBar={topBarMarkup}
                navigation={navigationMarkup}
                showMobileNavigation={mobileNavigationActive}
                onNavigationDismiss={toggleMobileNavigationActive}
                skipToContentTarget={skipToContentRef.current}
                logo={logoMarkup}
              >
                {pathToRouteMap.map((p, index) => {
                  let SelectedComponent = p.component
                  return (
                    <Route exact path={p.path} key={index}>
                      <Page title={p.name}>
                        <SelectedComponent
                          shopify_domain={props.shopify_domain}
                          {...(p.extraData || {})}
                        />
                      </Page>
                    </Route>
                  )
                })}
              </Frame>
            )}
          </>
        )}
      </Switch>
    </>
  )
}

export default withRouter(CustomerPortalMain)
